// type: pixel scale (paper notes over the world; see ui/kit.css)
import './toast-rack.css';
import { useToast } from '../../hooks/useToast';
import { Z } from '../../lib/zIndex';
import { hashStr } from '../../world/life';

/**
 * Notes that land in the room: a slip of paper drops in from above, turns as it falls and comes to rest a little
 * askew on the floor of the scene, pinned by a brass tack. The kind reads from the slip's tint and its rule line, not
 * from colour alone: an error slip also carries a red seal. Reduced motion places the slip without the fall.
 *
 * ``placement`` picks the bottom corner. The site shell keeps the right; the
 * chamber asks for the left, because its right column is the Question
 * Presented and the submit control, and a toast parked there (a denied
 * microphone, say) sat on top of the question for as long as it lived. The
 * left corner is the composer's own hint line, which a toast about the
 * microphone speaks for anyway.
 */
export default function ToastRack({ placement = 'right' }: { placement?: 'left' | 'right' } = {}) {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className={`toast-rack toast-rack-${placement}`}
      style={{ zIndex: Z.toast, [placement]: 20 }}
    >
      {toasts.map(t => {
        // each slip lands at its own angle, the same one every render
        const tilt = ((hashStr(String(t.id)) % 7) - 3) * 0.5;
        return (
          <div
            key={t.id}
            className={`toast-item toast-${t.kind}`}
            role={t.kind === 'error' ? 'alert' : 'status'}
            aria-atomic="true"
            style={{ ['--tilt' as string]: `${tilt}deg` }}
          >
            <span className="toast-tack" aria-hidden="true" />
            <span className="toast-msg">{t.message}</span>
            <button
              type="button"
              className="toast-dismiss"
              onClick={() => dismissToast(t.id)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}
